'use client'

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import clsx from 'clsx'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '@/store'
import { selectIsAuthenticated, selectCurrentUser } from '@/store/user/slice'
import { getAllTags } from '@/store/categories/slice'
import { selectMemberInfo, selectJoinedGroups } from '@/store/member/slice'
import { setActiveTab, selectActiveTab } from '@/store/notification/slice'
import { useNotifications } from '@/hooks/useNotifications'
import Badge from '@/components/Shared/Badge'
import GlobalMenu from '@/components/Shared/GlobalMenu'
import LogoBtn from '@/components/Shared/LogoBtn'
import CustomAccordion from '@/components/Shared/CustomAccordion'
import { useAuth } from '@/hooks/useAuth'
import { Company } from '@/types/company'
import { Category } from '@/types/category'
import styles from './styles.module.scss'

interface SideNavProps {
  onCloseMenu?: () => void
  shouldLoadData?: boolean
}

interface MemberData {
  following: {
    companies: Company[]
    categories: Category[]
  }
  favoriteStoryIds: number[]
}

/**
 * Side navigation component for member pages
 */
export default function SideNav({ onCloseMenu, shouldLoadData = true }: SideNavProps) {
  const dispatch = useDispatch<AppDispatch>()
  const pathname = usePathname()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const currentUser = useSelector(selectCurrentUser)
  const categories = useSelector((state: RootState) => state.categories.categorization)
  const memberInfo = useSelector(selectMemberInfo)
  const joinedGroups = useSelector(selectJoinedGroups)
  const { logout } = useAuth()
  const storeActiveTab = useSelector(selectActiveTab)
  const { noticesData, infoData } = useNotifications()
  const noticesBadge = noticesData ? !noticesData.confirmed : false
  const infoBadge = infoData ? !infoData.confirmed : false

  const [member, setMember] = useState<MemberData>({
    following: { companies: [], categories: [] },
    favoriteStoryIds: [],
  })

  // 全公開記事数 = 0 の企業はSideNav導線から除外する(該当企業ページは404になるため)
  const hiddenCompanyOpenUrls = useMemo(
    () =>
      new Set(
        joinedGroups
          ?.filter((g) => g.company.publicContentsCount === 0)
          .map((g) => g.company.openUrl) ?? [],
      ),
    [joinedGroups],
  )

  const visibleFollowingCompanies = useMemo(
    () =>
      member.following.companies.filter((company) => !hiddenCompanyOpenUrls.has(company.openUrl)),
    [member.following.companies, hiddenCompanyOpenUrls],
  )
  const [openPanel, setOpenPanel] = useState<boolean[]>([])
  const [openPanelCategory, setOpenPanelCategory] = useState<boolean[]>([])
  const [isClient, setIsClient] = useState(false)
  const [currentQueryParams, setCurrentQueryParams] = useState<URLSearchParams | null>(null)

  // Define mypage menu - use useMemo to prevent hydration mismatch
  const mypageMenu = useMemo(
    () => [
      {
        name: 'マイページ',
        path: '/members/mypage',
        icon: 'icon-font-icon_member',
        isSvg: true,
      },
      {
        name: 'マイ本棚',
        path: '/members/bookshelf',
        num: isClient ? member.favoriteStoryIds?.length || 0 : 0,
        icon: 'icon-font-icon_mybook',
      },
      {
        name: '最近見たページ',
        path: '/members/history',
        icon: 'icon-font-icon_history',
      },
      {
        name: 'フォロー中',
        path: '/members/follow',
        icon: 'icon-font-icon_bookmark',
      },
    ],
    [isClient, member.favoriteStoryIds?.length],
  )

  const roleModelFinderMenu = [
    {
      name: 'ロールモデル診断を行う',
      path: '/role-model-finder',
    },
    {
      name: '診断履歴',
      path: '/members/role-model-finder',
    },
  ]

  // Define other menu items
  const otherMenu = [
    {
      name: 'フォローレコメンド',
      path: '/members/recommend',
      className: '',
    },
    {
      name: 'よくある質問',
      path: '/faq',
      className: '',
    },
    {
      name: 'プロフィール',
      path: '/members/profile?type=profile',
      className: 'pcnone',
    },
    {
      name: '通知',
      path: '/members/profile?type=notification',
      className: 'pcnone',
    },
  ]

  // Filter other menu based on auth status
  const filteredOtherMenu = isAuthenticated
    ? otherMenu
    : otherMenu.filter(
        (item) =>
          item.name !== 'フォローレコメンド' &&
          item.name !== 'プロフィール' &&
          item.name !== '通知',
      )

  const loadMemberData = useCallback(async () => {
    if (!currentUser?.id) return

    // Redux storeからデータを使用
    if (memberInfo && memberInfo.memberId === currentUser.id) {
      // Use existing data from Redux
      setMember({
        following: memberInfo.following,
        favoriteStoryIds: memberInfo.favoriteStoryIds || [],
      })
      return
    }

    // HeaderでAPI取得が失敗した場合はstoreの初期値を使用
  }, [currentUser?.id, memberInfo])

  // Client-side flag
  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentQueryParams(new URLSearchParams(window.location.search))
    }
  }, [pathname])

  // Load categories data
  useEffect(() => {
    if (categories.length === 0) {
      dispatch(getAllTags())
    }
  }, [dispatch, categories.length])

  // Load member data when authenticated and when data should be loaded
  useEffect(() => {
    if (isAuthenticated && currentUser?.id && shouldLoadData) {
      loadMemberData()
    }
  }, [isAuthenticated, currentUser?.id, shouldLoadData, loadMemberData])

  const closeMenu = () => {
    if (onCloseMenu) {
      onCloseMenu()
    }
  }

  const togglePanel = (index: number, type: 'company' | 'category') => {
    if (type === 'company') {
      const newOpenPanel = [...openPanel]
      newOpenPanel[index] = !newOpenPanel[index]
      setOpenPanel(newOpenPanel)
    } else {
      const newOpenPanelCategory = [...openPanelCategory]
      newOpenPanelCategory[index] = !newOpenPanelCategory[index]
      setOpenPanelCategory(newOpenPanelCategory)
    }
  }

  const handleLogout = useCallback(async () => {
    await logout()
    if (onCloseMenu) {
      onCloseMenu()
    }
  }, [logout, onCloseMenu])

  // パラメータを含めてパスが一致するかチェック
  const isActiveLink = useCallback(
    (itemPath: string) => {
      const [itemPathname, itemQuery] = itemPath.split('?')

      // 一致しない
      if (pathname !== itemPathname) return false

      // パラメータがない
      if (!itemQuery) return true

      // パラメータがまだ取得されていない場合は非アクティブ
      if (!currentQueryParams) return false

      // パラメータ比較
      const itemParams = new URLSearchParams(itemQuery)
      const itemType = itemParams.get('type')
      const currentType = currentQueryParams.get('type')

      return itemType === currentType
    },
    [pathname, currentQueryParams],
  )

  // Browser detection (replicated from Vue component)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (navigator.userAgent.includes('Windows')) {
        document.body.classList.add('is-windows')

        // Edge detection
        const isEdge = /Edg/.test(navigator.userAgent)
        if (isEdge) document.body.classList.add('is-edge')
      }
    }
  }, [])

  return (
    <div className={clsx(styles.sidenavWrapper, 'sidenav')}>
      {/* SP: close button */}
      {onCloseMenu && (
        <button type='button' className={styles.closeMenuButton} onClick={closeMenu}>
          閉じる
        </button>
      )}

      {/* Authenticated user menu */}
      {isAuthenticated && (
        <>
          <ul className={styles.sidenavList}>
            {mypageMenu.map((item) => (
              <li key={item.name} className={styles.sidenavItem} onClick={closeMenu}>
                <Link
                  href={item.path}
                  className={clsx(styles.sidenavLink, styles.menuLink, {
                    [styles.active]: pathname === item.path,
                  })}
                >
                  {item.isSvg ? (
                    <svg
                      className={clsx(styles.sidenavItemImg, styles.memberIcon)}
                      viewBox='0 0 24 24'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' />
                    </svg>
                  ) : (
                    <i className={clsx(item.icon, styles.sidenavItemImg, styles.memberIcon)} />
                  )}
                  <p className={styles.sidenavItemName}>
                    {item.name}
                    {item.num ? <span>({item.num})</span> : null}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
          <ul className={clsx(styles.sidenavList, styles.notices)}>
            <li
              className={styles.sidenavItem}
              onClick={() => {
                dispatch(setActiveTab('notices'))
                closeMenu()
              }}
            >
              <Link
                href='/info'
                className={clsx(styles.sidenavLink, {
                  [styles.active]: pathname === '/info' && storeActiveTab === 'notices',
                })}
              >
                更新通知
                {noticesBadge && <Badge used='sidenav' />}
              </Link>
            </li>
            <li
              className={styles.sidenavItem}
              onClick={() => {
                dispatch(setActiveTab('info'))
                closeMenu()
              }}
            >
              <Link
                href='/info'
                className={clsx(styles.sidenavLink, {
                  [styles.active]: pathname === '/info' && storeActiveTab === 'info',
                })}
              >
                お知らせ
                {infoBadge && <Badge used='sidenav' />}
              </Link>
            </li>
          </ul>
          <ul className={styles.sidenavList}>
            <li className={styles.sidenavTitle}>ロールモデル診断</li>
            {roleModelFinderMenu.map((item) => (
              <li key={item.name} className={styles.sidenavItem} onClick={closeMenu}>
                <Link href={item.path} className={styles.sidenavLink}>
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>

          {/* Member groups */}
          {joinedGroups?.length > 0 && (
            <ul className={styles.sidenavList}>
              <li className={styles.sidenavTitle}>グループ</li>
              {joinedGroups.map((group) => (
                <li key={group.id} className={styles.sidenavItem} onClick={closeMenu}>
                  <Link
                    href={`/${group.company.openUrl}/group/${group.id}`}
                    className={clsx(styles.sidenavLink, {
                      [styles.active]: pathname === `/${group.company.openUrl}/group/${group.id}`,
                    })}
                  >
                    {group.company.imageUrl && (
                      <img
                        src={group.company.imageUrl}
                        className={styles.sidenavItemImg}
                        alt={group.company.name}
                        loading='lazy'
                      />
                    )}
                    <span className={styles.sidenavItemName}>
                      <span className={styles.groupTitle}>{group.name}</span>
                      <span className={styles.groupCompanyName}>{group.company.name}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Non-authenticated: custom navigation menu */}
      {!isAuthenticated && (
        <>
          {/* SP: ログイン・新規作成・はじめての方へ */}
          <div className={styles.authButtonsSection}>
            <LogoBtn type='noImage' text='ログイン' link='/login' used='sidenavAuth' />
            <p className={styles.authSignupText}>タレントブックアカウントをお持ちでない場合</p>
            <LogoBtn
              type='talentbook'
              text='アカウント新規作成'
              link='/signup'
              used='sidenavAuth'
            />
          </div>
          {/* PC: はじめての方へボタン */}
          <div className={clsx(styles.guideButtonSection, styles.spnone)}>
            <LogoBtn
              type='noImage'
              text='🔰 はじめての方へ'
              link='/talentbook_guide'
              used='sidenavGuide'
            />
          </div>
          <ul className={styles.sidenavList}>
            {/* SP: はじめての方へリンク */}
            <li className={clsx(styles.sidenavItem, styles.pcnone)} onClick={closeMenu}>
              <Link
                href='/talentbook_guide'
                className={clsx(styles.sidenavLink, styles.menuLink, {
                  [styles.active]: pathname === '/talentbook_guide',
                })}
              >
                <span className={styles.guideIcon}>🔰</span>
                <p className={styles.sidenavItemName}>はじめての方へ</p>
              </Link>
            </li>
            <li className={styles.sidenavItem} onClick={closeMenu}>
              <Link
                href='/contents'
                className={clsx(styles.sidenavLink, styles.menuLink, {
                  [styles.active]: pathname === '/contents',
                })}
              >
                新着記事
              </Link>
            </li>
            <li className={styles.sidenavItem} onClick={closeMenu}>
              <Link
                href='/companies'
                className={clsx(styles.sidenavLink, styles.menuLink, {
                  [styles.active]: pathname === '/companies',
                })}
              >
                企業検索
              </Link>
            </li>
            <li className={styles.sidenavItem} onClick={closeMenu}>
              <Link
                href='/feature'
                className={clsx(styles.sidenavLink, styles.menuLink, {
                  [styles.active]: pathname === '/feature',
                })}
              >
                特集
              </Link>
            </li>
          </ul>
        </>
      )}

      {/* Authenticated: GlobalMenu for mobile only */}
      {isAuthenticated && (
        <div className={styles.pcnone}>
          <GlobalMenu used='sidenav' onCloseMenu={closeMenu} memberId={currentUser?.id} />
        </div>
      )}

      {/* Following companies */}
      {visibleFollowingCompanies.length > 0 && (
        <ul className={styles.sidenavList}>
          <li className={styles.sidenavTitle}>フォロー中企業</li>
          {visibleFollowingCompanies.slice(0, 7).map((item) => (
            <li key={item.id} className={styles.sidenavItem} onClick={closeMenu}>
              <Link
                href={`/${item.openUrl}`}
                className={clsx(styles.sidenavLink, {
                  [styles.active]: pathname === `/${item.openUrl}`,
                })}
              >
                <img
                  src={item.imageUrl}
                  className={styles.sidenavItemImg}
                  alt={item.name}
                  loading='lazy'
                />
                <span className={styles.sidenavItemName}>{item.name}</span>
              </Link>
            </li>
          ))}
          {visibleFollowingCompanies.length > 7 && (
            <li className={styles.sidenavItem}>
              <CustomAccordion
                title='すべて見る'
                isExpanded={openPanel[0]}
                onToggle={() => togglePanel(0, 'company')}
                titleClassName={styles.showAll}
              >
                <ul className={styles.showAllList}>
                  {visibleFollowingCompanies.slice(7).map((item) => (
                    <li key={item.id} className={styles.showAllItem} onClick={closeMenu}>
                      <Link
                        href={`/${item.openUrl}`}
                        className={clsx(styles.sidenavLink, {
                          [styles.active]: pathname === `/${item.openUrl}`,
                        })}
                      >
                        <img
                          src={item.imageUrl}
                          className={styles.sidenavItemImg}
                          alt={item.name}
                          loading='lazy'
                        />
                        <span className={styles.sidenavItemName}>{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </CustomAccordion>
            </li>
          )}
        </ul>
      )}

      {/* All categories */}
      <ul className={styles.sidenavList}>
        <li className={styles.sidenavTitle}>すべてのカテゴリ</li>
        <li className={styles.sidenavItem}>
          <div className={styles.expansionPanels}>
            {/* Following categories */}
            {member.following.categories.length > 0 && (
              <CustomAccordion
                title='フォロー中'
                isExpanded={openPanelCategory[0]}
                onToggle={() => togglePanel(0, 'category')}
                titleClassName={styles.categoryTitle}
              >
                <ul className={styles.recordList}>
                  {member.following.categories.map((item) => (
                    <li key={item.id} className={styles.recordItem} onClick={closeMenu}>
                      <Link
                        href={`/categories/${item.slug}`}
                        className={clsx(styles.recordLink, {
                          [styles.active]: pathname === `/categories/${item.slug}`,
                        })}
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </CustomAccordion>
            )}

            {/* All categories */}
            {categories.map((item, index) => (
              <CustomAccordion
                key={item.categoryName}
                title={item.categoryName}
                isExpanded={openPanelCategory[index + 1]}
                onToggle={() => togglePanel(index + 1, 'category')}
                titleClassName={clsx(styles.categoryTitle, styles.allCategoriesPanel)}
              >
                <ul className={styles.recordList}>
                  {item.records.map((record) => (
                    <li key={record.id} className={styles.recordItem} onClick={closeMenu}>
                      <Link
                        href={`/categories/${record.slug}`}
                        className={clsx(styles.recordLink, {
                          [styles.active]: pathname === `/categories/${record.slug}`,
                        })}
                      >
                        {record.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </CustomAccordion>
            ))}
          </div>
        </li>
      </ul>

      {/* Other menu */}
      <ul className={styles.sidenavList}>
        {filteredOtherMenu.map((item) => (
          <li
            key={item.path}
            className={clsx(item.className, styles.sidenavItem)}
            onClick={closeMenu}
          >
            <Link
              href={item.path}
              className={clsx(styles.sidenavLink, styles.linkFaq, {
                [styles.active]: isActiveLink(item.path),
              })}
            >
              {item.name}
            </Link>
          </li>
        ))}
      </ul>

      {/* Logout button (authenticated only) */}
      {isAuthenticated && (
        <div className={clsx(styles.bottomButton, styles.sidenavList, styles.pcnone)}>
          <button onClick={handleLogout} className={styles.logoutButton}>
            <i className='icon-font-icon_logout'></i>
            ログアウト
          </button>
        </div>
      )}
    </div>
  )
}
